<template>
  <div class="unsubscribe-content">
    <!-- Initial State: Confirm Button -->
    <div v-if="!status && !isLoading" class="confirm-state">
      <div class="icon mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="text-dark">
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
          <polyline points="22,6 12,13 2,6"></polyline>
          <line x1="15" y1="15" x2="19" y2="19"></line>
          <line x1="19" y1="15" x2="15" y2="19"></line>
        </svg>
      </div>
      <h1 class="title">{{ t('newsletter.unsubscribe.title') }}</h1>
      <button @click="handleUnsubscribe" class="btn-confirm">
        {{ t('newsletter.unsubscribe.confirm_btn') }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="spinner"></div>
      <p>{{ t('newsletter.unsubscribe.loading') }}</p>
    </div>

    <!-- Success State -->
    <div v-else-if="status === 'success'" class="success-state">
      <div class="success-icon mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="text-teal">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      </div>
      <h1 class="success-title">
        {{ t('newsletter.unsubscribe.success') }}
      </h1>
      <div class="actions mt-4">
        <router-link to="/" class="btn-back-home">
          {{ t('newsletter.back_to_home') }}
        </router-link>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="status === 'error'" class="error-state">
      <div class="error-icon mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="text-danger">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </div>
      <h1 class="error-title">
        {{ t('newsletter.unsubscribe.error') }}
      </h1>
      <div class="error-actions mt-4">
        <router-link to="/" class="btn-back-home">
          {{ t('newsletter.back_to_home') }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'
import apiClient from '@/services/api'

const route = useRoute()
const { t } = useLanguage()

const isLoading = ref(false)
const status = ref(null) // 'success' | 'error'

const handleUnsubscribe = async () => {
  const email = route.query.email
  const token = route.query.token

  if (!email || !token) {
    status.value = 'error'
    return
  }

  isLoading.value = true
  try {
    await apiClient.post('/newsletter/unsubscribe', {
      email,
      token
    })
    status.value = 'success'
  } catch (err) {
    console.error('[UNSUBSCRIBE] Error:', err)
    status.value = 'error'
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  // If we want it to be automatic, we could call handleUnsubscribe here
  // But usually for unsubscribe a confirmation button is safer to avoid accidental clicks from mail scanners
  // The user asked "Al caricamento (o tramite un pulsante...)", I'll provide the button as per UI best practice 
  // but we can make it automatic if required. Let's stick with the button for better UX.
})
</script>

<style scoped>
.unsubscribe-content {
  width: 100%;
  text-align: center;
}

.confirm-state, .loading-state, .success-state, .error-state {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.spinner {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #111111;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.title, .success-title, .error-title {
  font-size: 18px; /* Ridotto per maggiore eleganza */
  font-weight: 900;
  color: #000000;
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  line-height: 1.4;
}

.text-teal {
  color: var(--bright-teal);
}

.btn-confirm, .btn-back-home {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px 30px;
  font-size: 13px;
  font-weight: 700; /* Leggermente meno bold */
  color: white;
  background: #000000;
  border: none;
  border-radius: 2px; /* Coerente con il box */
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 3px; /* Più spaziatura per l'eleganza */
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  cursor: pointer;
}

.btn-confirm:hover, .btn-back-home:hover {
  background: #1a1a1a;
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.15);
  color: white;
}

@media (max-width: 600px) {
  .title, .success-title, .error-title {
    font-size: 16px;
  }
}
</style>
